import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Reveal } from '@/components/Reveal';
import { SectionHeading } from '@/components/SectionHeading';
import { COURSE_BY_ID, LESSON_BY_ID } from '@/lib/data';
import { useAppStore } from '@/lib/store';
import type { ErrorType, MistakeRecord, StudyMode } from '@/lib/types';

const ERROR_META: Record<ErrorType, { label: string; emoji: string; tone: string }> = {
  grammar: { label: '语法', emoji: '📐', tone: 'text-honey-300 bg-honey-500/15 border-honey-500/30' },
  vocab: { label: '词汇', emoji: '📖', tone: 'text-mint-300 bg-mint-500/15 border-mint-500/30' },
  spelling: { label: '拼写', emoji: '✏️', tone: 'text-cream-200 bg-white/[0.06] border-white/[0.12]' },
  order: { label: '语序', emoji: '🔀', tone: 'text-honey-300 bg-honey-500/15 border-honey-500/30' },
  pronunciation: { label: '发音', emoji: '🎙️', tone: 'text-ember-300 bg-ember-500/15 border-ember-500/30' },
};

const MODE_LABEL: Record<StudyMode, string> = {
  puzzle: '🧩 拼图',
  typing: '⌨️ 打字',
  speaking: '🎙️ 口语',
};

function dayKey(ts: number): string {
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function friendlyDay(key: string): string {
  const today = dayKey(Date.now());
  const yest = dayKey(Date.now() - 86400000);
  if (key === today) return '今天';
  if (key === yest) return '昨天';
  const d = new Date(key);
  return `${d.getMonth() + 1}月${d.getDate()}日`;
}

export function MistakesPage(): JSX.Element {
  const mistakes = useAppStore((s) => s.mistakes);
  const reviewMistake = useAppStore((s) => s.reviewMistake);
  const clearMistake = useAppStore((s) => s.clearMistake);

  // v4: group by day for readability.
  const grouped = useMemo(() => {
    const map = new Map<string, MistakeRecord[]>();
    for (const m of mistakes) {
      const k = dayKey(m.timestamp);
      const arr = map.get(k) ?? [];
      arr.push(m);
      map.set(k, arr);
    }
    return Array.from(map.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [mistakes]);

  return (
    <div className="space-y-10">
      <Reveal>
        <SectionHeading
          eyebrow="Mistake Book · 错题本"
          title={<>每个错题， <span className="text-gradient-warm">都是下一次的进步机会</span></>}
          subtitle="AI 会把每次出错分门别类、解释清楚、加进复习队列。下次再考你的，就是你薄弱的地方。"
          align="left"
        />
      </Reveal>

      {mistakes.length === 0 ? (
        <Reveal>
          <div className="text-center py-20 rounded-3xl border border-white/[0.06] bg-white/[0.02]">
            <div className="text-7xl mb-6">📭</div>
            <div className="font-display text-2xl font-bold text-white">还没有错题</div>
            <p className="mt-2 text-cream-400">先去练习，AI 会把每一次出错自动归档到这里</p>
            <div className="mt-6">
              <Link to="/app/courses" className="inline-flex items-center gap-1 text-honey-300 hover:text-honey-200">浏览课程 →</Link>
            </div>
          </div>
        </Reveal>
      ) : (
        <div className="space-y-10">
          {grouped.map(([day, items]) => (
            <Reveal key={day}>
              <div>
                <div className="flex items-baseline gap-3 mb-4">
                  <h2 className="font-display text-xl font-bold text-white">{friendlyDay(day)}</h2>
                  <span className="text-xs text-cream-400 font-arcade tracking-widest">{day}</span>
                  <span className="text-xs text-cream-500">· {items.length} 题</span>
                </div>
                <div className="space-y-3">
                  {items.map((m) => {
                    const meta = ERROR_META[m.errorType];
                    const lesson = LESSON_BY_ID[m.lessonId];
                    const course = COURSE_BY_ID[m.courseId];
                    return (
                      <div key={m.id} className="rounded-3xl border border-white/[0.06] bg-white/[0.02] p-5">
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`text-xs px-2 py-0.5 rounded-md border ${meta.tone}`}>
                              {meta.emoji} {meta.label}
                            </span>
                            <span className="text-xs text-cream-400">{MODE_LABEL[m.mode]}</span>
                            {course !== undefined && (
                              <Link to={`/app/courses/${course.id}`} className="text-xs text-cream-400 hover:text-white">· {course.title}</Link>
                            )}
                            {lesson !== undefined && (
                              <span className="text-xs text-cream-500">· {lesson.title}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {!m.reviewed && (
                              <button
                                onClick={() => reviewMistake(m.id)}
                                className="text-xs px-2 py-1 rounded-full bg-mint-500/15 text-mint-300 hover:bg-mint-500/25 transition-colors"
                              >
                                标记已复习
                              </button>
                            )}
                            <button
                              onClick={() => clearMistake(m.id)}
                              className="text-xs px-2 py-1 rounded-full text-cream-400 hover:text-white hover:bg-white/[0.06] transition-colors"
                              aria-label="删除"
                            >
                              ×
                            </button>
                          </div>
                        </div>

                        <div className="grid gap-2 text-sm">
                          <div className="flex gap-2">
                            <span className="text-cream-400 shrink-0">你的：</span>
                            <span className="font-mono text-ember-200 break-all">{m.userInput || '(空)'}</span>
                          </div>
                          <div className="flex gap-2">
                            <span className="text-cream-400 shrink-0">正确：</span>
                            <span className="font-mono text-mint-200 break-all">{m.correct}</span>
                          </div>
                        </div>

                        {lesson !== undefined && (
                          <div className="mt-3 flex gap-2 flex-wrap">
                            <Link to={`/app/study/${m.mode}/${m.lessonId}`} className="text-xs text-honey-300 hover:text-honey-200">
                              重做这道 →
                            </Link>
                            <span className="text-xs text-cream-500">
                              · {new Date(m.timestamp).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      )}
    </div>
  );
}
